// components/Outreach/CreateIntentionsForCampaign.js
import React, { useState, useEffect } from "react";
import { getCookie } from "../../services/cookie";

const API_BASE = "https://flautreach.com/flautreach/api";

const CONDITION_TYPES = [
  { value: "RANDOM_SUBSET", label: "🎲 Случайный процент", hasPercent: true },
  { value: "WITHOUT_INTENTIONS", label: "🚫 Без intentions", hasPercent: false },
  { value: "WITHOUT_REPLIES", label: "💬 Без replies", hasPercent: false },
  { value: "WITHOUT_EXECUTIONS", label: "⚡ Без executions", hasPercent: false },
  { value: "EXCLUDE_CAMPAIGNS", label: "🚫 Исключить кампании", hasCampaigns: true },
  { value: "INCLUDE_CAMPAIGNS", label: "✅ Только кампании", hasCampaigns: true },
];

export function CreateIntentionsForCampaign({ campaign, onBack, onCreated, add }) {
  const token = () => getCookie("authToken");
  const getHeaders = () => {
    const t = token();
    const headers = { "Content-Type": "application/json" };
    if (t) {
      headers["Authorization"] = `Bearer ${t}`;
    }
    return headers;
  };

  const [tasks, setTasks] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [taskInfo, setTaskInfo] = useState(null);
  const [loadingTaskInfo, setLoadingTaskInfo] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Состояния для условий
  const [useAllTargets, setUseAllTargets] = useState(false);
  const [conditions, setConditions] = useState([]);
  const [showConditions, setShowConditions] = useState(false);
  const [campaignSearch, setCampaignSearch] = useState("");

  useEffect(() => {
    loadTasks();
    loadCampaigns();
  }, []);

  const loadTasks = async () => {
    setLoadingTasks(true);
    try {
      const resp = await fetch(`${API_BASE}/tasks/`, { headers: getHeaders() });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setTasks(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error("Error loading tasks:", err);
      add?.("Ошибка загрузки задач", "error");
    } finally {
      setLoadingTasks(false);
    }
  };

  const loadCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const resp = await fetch(`${API_BASE}/outreach/campaigns/`, { headers: getHeaders() });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setCampaigns(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error("Error loading campaigns:", err);
      add?.("Ошибка загрузки кампаний", "error");
    } finally {
      setLoadingCampaigns(false);
    }
  };

  useEffect(() => {
    if (!selectedTaskId) { 
      setTaskInfo(null); 
      return; 
    }
    loadTaskInfo(selectedTaskId);
  }, [selectedTaskId]);

  const loadTaskInfo = async (taskId) => {
    setLoadingTaskInfo(true);
    const task = tasks.find((t) => t.id === taskId);
    const taskType = task?.task_type || "scraping";
    
    try {
      let total = 0;
      if (taskType === "filtering" || taskType === "scraping_filtering") {
        const url = `${API_BASE}/tasks/${taskId}/filtering-results/?regular_filter=passed&page_size=1`;
        const resp = await fetch(url, { headers: getHeaders() });
        if (resp.ok) {
          const data = await resp.json();
          total = data.pagination?.total || data.summary?.total_runs || 0;
        }
      } else {
        const url = `${API_BASE}/tasks/${taskId}/results/?status=success&page_size=1`;
        const resp = await fetch(url, { headers: getHeaders() });
        if (resp.ok) {
          const data = await resp.json();
          total = data.pagination?.total || 0;
        }
      }
      setTaskInfo({ name: task?.name || `#${taskId}`, task_type: taskType, total_results: total });
    } catch (err) {
      console.error("Error loading task info:", err);
      add?.("Ошибка загрузки информации о задаче", "error");
    } finally {
      setLoadingTaskInfo(false);
    }
  };

  // Функции для работы с условиями
  const addCondition = () => {
    setConditions([...conditions, { type: "", percent: null, campaign_ids: [] }]);
  };

  const removeCondition = (index) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const updateCondition = (index, field, value) => {
    const updated = [...conditions];
    updated[index][field] = value;
    setConditions(updated);
  };

  const handleCreate = async () => {
    console.log("=== HANDLE CREATE CALLED ===");
    console.log("Campaign:", campaign);
    console.log("Selected task:", selectedTaskId);
    console.log("Use all targets:", useAllTargets);
    console.log("Conditions:", conditions);
    
    if (!useAllTargets && !selectedTaskId) {
      add?.("Выберите задачу или отметьте 'Использовать все цели'", "error");
      return;
    }
    
    setCreating(true);
    try {
      const body = {
        campaign_id: campaign.id,
        conditions: conditions
          .filter(c => c.type) // Только заполненные условия
          .map(c => ({
            type: c.type,
            ...(c.percent && { percent: c.percent }),
            ...(c.campaign_ids?.length && { campaign_ids: c.campaign_ids }),
          })),
      };

      if (!useAllTargets && selectedTaskId) {
        body.source_task_id = selectedTaskId;
      }

      console.log("Request body:", body);

      // Определяем endpoint
      const hasConditions = body.conditions.length > 0;
      const endpoint = hasConditions || useAllTargets
        ? `${API_BASE}/outreach/intentions/create_with_conditions/`
        : `${API_BASE}/outreach/intentions/create_from_task/`;

      console.log("Endpoint:", endpoint);

      const resp = await fetch(endpoint, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(body),
      });

      console.log("Response status:", resp.status);
      
      const data = await resp.json().catch(() => ({}));
      console.log("Response data:", data);

      if (!resp.ok) {
        throw new Error(data.detail || `HTTP ${resp.status}: ${JSON.stringify(data)}`);
      }

      if (hasConditions || useAllTargets) {
        add?.(`Создано ${data.created} намерений (пропущено ${data.skipped})`, "success");
      } else {
        add?.(`Создано ${data.intentions_created || data.created || 0} намерений`, "success");
      }
      
      onCreated?.();
    } catch (err) {
      console.error("Error creating intentions:", err);
      add?.(err.message || "Ошибка", "error");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="outreach-create-wrap">
      <div className="rules-header">
        <div>
          <h2>Создать намерения для кампании</h2>
          <p>{campaign.name}</p>
        </div>
        <button className="btn-cancel" onClick={onBack}>← Назад</button>
      </div>

      {/* Источник целей */}
      <div className="section-card" style={{ marginBottom: 16 }}>
        <h3>Источник целей</h3>
        <div className="form-group">
          <label className="form-label" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input 
              type="checkbox" 
              checked={useAllTargets}
              onChange={(e) => {
                setUseAllTargets(e.target.checked);
                if (e.target.checked) {
                  setSelectedTaskId(null);
                  setTaskInfo(null);
                }
              }}
            />
            Использовать все активные цели
          </label>
        </div>
        
        {!useAllTargets && (
          <div className="form-group">
            <label className="form-label">Задача-источник</label>
            <select 
              className="form-input" 
              style={{ paddingLeft: 14 }} 
              value={selectedTaskId || ""} 
              onChange={(e) => setSelectedTaskId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">{loadingTasks ? "Загрузка..." : "-- Выберите задачу --"}</option>
              {tasks.map((t) => (
                <option key={t.id} value={t.id}>{t.name} ({t.task_type})</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Информация о задаче */}
      {selectedTaskId && !useAllTargets && (
        <div className="section-card" style={{ marginBottom: 16 }}>
          <h3>Информация</h3>
          {loadingTaskInfo ? (
            <div style={{ textAlign: "center", padding: 20 }}><div className="spinner"/></div>
          ) : taskInfo ? (
            <div style={{ display: "grid", gap: 12 }}>
              <div className="info-box">
                <span>📋</span>
                <div>
                  <strong>{taskInfo.name}</strong>
                  <p style={{ fontSize: 12 }}>Тип: {taskInfo.task_type}</p>
                </div>
              </div>
              <div className="info-box" style={{ 
                background: taskInfo.total_results > 0 ? "#f0fdf4" : "#fef2f2", 
                borderColor: taskInfo.total_results > 0 ? "#bbf7d0" : "#fecaca" 
              }}>
                <span>{taskInfo.total_results > 0 ? "✅" : "⚠️"}</span>
                <div>
                  <strong>{taskInfo.total_results.toLocaleString()} результатов</strong>
                  <p style={{ fontSize: 12 }}>
                    Будет создано {taskInfo.total_results.toLocaleString()} намерений
                    с каналами: {(campaign.platforms || []).map(p => {
                      const meta = { EMAIL: "📧", FB_MESSENGER: "💬", CONTACT_FORM: "📝" };
                      return (meta[p] || "") + " " + p;
                    }).join(", ")}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Условия фильтрации */}
      <div className="section-card" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3>Условия фильтрации</h3>
          <button 
            className="btn-primary" 
            style={{ width: "auto", padding: "8px 16px", fontSize: 13 }}
            onClick={() => {
              setShowConditions(!showConditions);
              if (!showConditions && conditions.length === 0) {
                addCondition();
              }
            }}
          >
            {showConditions ? "Скрыть условия" : "➕ Добавить условия"}
          </button>
        </div>

        {showConditions && (
          <div style={{ marginTop: 12 }}>
            <p className="section-sub" style={{ marginBottom: 12 }}>
              Условия применяются последовательно. Сабсет (процент) применяется в самом конце.
            </p>

            {conditions.map((cond, index) => {
              const condType = CONDITION_TYPES.find(c => c.value === cond.type);
              
              return (
                <div key={index} style={{
                  padding: 12,
                  background: "#f9fafb",
                  borderRadius: 8,
                  marginBottom: 8,
                }}>
                  <div style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}>
                    <select
                      className="form-input"
                      style={{ flex: 2, minWidth: 200 }}
                      value={cond.type}
                      onChange={(e) => updateCondition(index, "type", e.target.value)}
                    >
                      <option value="">-- Выберите условие --</option>
                      {CONDITION_TYPES.map(ct => (
                        <option key={ct.value} value={ct.value}>{ct.label}</option>
                      ))}
                    </select>

                    {condType?.hasPercent && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                          type="number"
                          className="form-input"
                          style={{ width: 100 }}
                          placeholder="%"
                          min="1"
                          max="100"
                          value={cond.percent || ""}
                          onChange={(e) => updateCondition(index, "percent", Number(e.target.value))}
                        />
                        <span style={{ fontSize: 14, color: "#6b7280" }}>%</span>
                      </div>
                    )}

                    <button 
                      className="btn-cancel" 
                      style={{ padding: "8px 10px", fontSize: 14, marginLeft: "auto" }}
                      onClick={() => removeCondition(index)}
                      title="Удалить условие"
                    >
                      ✕
                    </button>
                  </div>

                  {condType?.hasCampaigns && (
                    <div style={{ width: "100%", marginTop: 8 }}>
                      <input
                        type="text"
                        className="form-input"
                        style={{ marginBottom: 8 }}
                        placeholder="🔍 Поиск кампании..."
                        value={campaignSearch}
                        onChange={(e) => setCampaignSearch(e.target.value)}
                      />
                      <div style={{
                        maxHeight: 200,
                        overflowY: "auto",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: 4,
                        background: "white",
                      }}>
                        {campaigns
                          .filter(c => c.name.toLowerCase().includes(campaignSearch.toLowerCase()))
                          .map(c => (
                            <label key={c.id} style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "6px 8px",
                              cursor: "pointer",
                              borderRadius: 4,
                              background: cond.campaign_ids?.includes(c.id) ? "#f0fdf4" : "transparent",
                            }}>
                              <input
                                type="checkbox"
                                checked={cond.campaign_ids?.includes(c.id) || false}
                                onChange={() => {
                                  const currentIds = cond.campaign_ids || [];
                                  const newIds = currentIds.includes(c.id)
                                    ? currentIds.filter(id => id !== c.id)
                                    : [...currentIds, c.id];
                                  updateCondition(index, "campaign_ids", newIds);
                                }}
                              />
                              <span style={{ fontSize: 13 }}>{c.name}</span>
                              {cond.campaign_ids?.includes(c.id) && (
                                <span style={{ marginLeft: "auto", color: "#16a34a" }}>✓</span>
                              )}
                            </label>
                          ))}
                        {campaigns.filter(c => c.name.toLowerCase().includes(campaignSearch.toLowerCase())).length === 0 && (
                          <p style={{ textAlign: "center", color: "#9ca3af", padding: 12 }}>
                            Ничего не найдено
                          </p>
                        )}
                      </div>
                      {cond.campaign_ids?.length > 0 && (
                        <p style={{ fontSize: 12, marginTop: 4, color: "#16a34a" }}>
                          Выбрано: {cond.campaign_ids.length}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            <button 
              className="btn-secondary" 
              style={{ width: "auto", padding: "6px 14px", fontSize: 13, marginTop: 4 }}
              onClick={addCondition}
            >
              + Ещё условие
            </button>
          </div>
        )}
      </div>

      {/* Действия */}
      <div className="form-actions" style={{ gap: 12 }}>
        <button className="btn-cancel" onClick={onBack}>← Назад</button>
        <button 
          className="btn-primary" 
          style={{ width: "auto", padding: "13px 40px" }}
          disabled={creating || (!useAllTargets && !selectedTaskId)}
          onClick={handleCreate}
        >
          {creating ? "⏳ Создание..." : "🚀 Создать намерения"}
        </button>
      </div>
    </div>
  );
}